/**
 * Pure JavaScript SHA256 implementation
 * Used as fallback when crypto.subtle is not available (non-HTTPS contexts)
 */

/**
 * SHA256 constants
 */
const K: number[] = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
];

/**
 * Initial hash values
 */
const H: number[] = [
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
  0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19
];

/**
 * Rotate right
 */
function rotr(n: number, b: number): number {
  return (n >>> b) | (n << (32 - b));
}

/**
 * SHA256 compression function
 */
function sha256Transform(W: number[], H: number[]): void {
  let a = H[0];
  let b = H[1];
  let c = H[2];
  let d = H[3];
  let e = H[4];
  let f = H[5];
  let g = H[6];
  let h = H[7];
  
  for (let j = 0; j < 64; j++) {
    if (j >= 16) {
      const s0 = rotr(W[j - 15], 7) ^ rotr(W[j - 15], 18) ^ (W[j - 15] >>> 3);
      const s1 = rotr(W[j - 2], 17) ^ rotr(W[j - 2], 19) ^ (W[j - 2] >>> 10);
      W[j] = (W[j - 16] + s0 + W[j - 7] + s1) >>> 0;
    }
    
    const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
    const ch = (e & f) ^ ((~e) & g);
    const temp1 = (h + S1 + ch + K[j] + W[j]) >>> 0;
    
    const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
    const maj = (a & b) ^ (a & c) ^ (b & c);
    const temp2 = (S0 + maj) >>> 0;
    
    h = g;
    g = f;
    f = e;
    e = (d + temp1) >>> 0;
    d = c;
    c = b;
    b = a;
    a = (temp1 + temp2) >>> 0;
  }
  
  H[0] = (H[0] + a) >>> 0;
  H[1] = (H[1] + b) >>> 0;
  H[2] = (H[2] + c) >>> 0;
  H[3] = (H[3] + d) >>> 0;
  H[4] = (H[4] + e) >>> 0;
  H[5] = (H[5] + f) >>> 0;
  H[6] = (H[6] + g) >>> 0;
  H[7] = (H[7] + h) >>> 0;
}

/**
 * Calculate SHA256 hash from bytes
 */
export function sha256Fallback(bytes: Uint8Array): string {
  const H_copy = [...H];
  const msgLen = bytes.length;
  const msgBitLen = msgLen * 8;
  
  // Padding
  const padLen = (msgLen % 64 < 56) ? 56 - (msgLen % 64) : 120 - (msgLen % 64);
  const padded = new Uint8Array(msgLen + padLen + 8);
  padded.set(bytes);
  padded[msgLen] = 0x80;
  
  // Append length (64-bit big-endian)
  const dataView = new DataView(padded.buffer);
  dataView.setUint32(padded.length - 8, 0, false); // high 32 bits
  dataView.setUint32(padded.length - 4, msgBitLen >>> 0, false); // low 32 bits
  
  // Process blocks
  for (let offset = 0; offset < padded.length; offset += 64) {
    const W = new Array(64);
    
    // Copy block into W[0..15]
    for (let i = 0; i < 16; i++) {
      W[i] = dataView.getUint32(offset + i * 4, false);
    }
    
    sha256Transform(W, H_copy);
  }
  
  // Convert to hex string
  let hex = '';
  for (let i = 0; i < 8; i++) {
    hex += H_copy[i].toString(16).padStart(8, '0');
  }
  
  return hex;
}